import { FC, useContext, useMemo, useRef, useState, useEffect } from "react";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import PageSection from "@app/shared/PageSection";
import { ProviderHubBasicFilters } from "@app/projects/network-assessment/provider-hub/ProvidersHubBasicFilters";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { formatGapImpactCount, formatSpecialtyWithCode, SortHospitalByOptionToDisplay, NO_ACTIONS_APPLIED_LABEL } from "@app/projects/network-assessment/provider-hub/utility";
import { ProviderHubHospitals, ProviderHubHospitalSortColumn, ProviderHubHospitalTableData, ProviderSearchByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ShowFullDataToggle } from "@app/projects/network-assessment/provider-hub/ShowFullDataToggle";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import ProviderHubGenerateReportFlyout from "@app/projects/network-assessment/provider-hub/ProviderHubGenerateReportFlyout";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";

// Gap Impact "Sort by" options: the single combined pair vs. the four region-status pairs.
const combinedGapImpactSortOptions: ProviderHubHospitalSortColumn[] = [
  ProviderHubHospitalSortColumn.GapImpactDescending,
  ProviderHubHospitalSortColumn.GapImpactAscending,
];

const breakdownGapImpactSortOptions: ProviderHubHospitalSortColumn[] = [
  ProviderHubHospitalSortColumn.ActiveGapImpactDescending,
  ProviderHubHospitalSortColumn.ActiveGapImpactAscending,
  ProviderHubHospitalSortColumn.PendingGapImpactDescending,
  ProviderHubHospitalSortColumn.PendingGapImpactAscending,
  ProviderHubHospitalSortColumn.ApprovedGapImpactDescending,
  ProviderHubHospitalSortColumn.ApprovedGapImpactAscending,
  ProviderHubHospitalSortColumn.OpportunityGapImpactDescending,
  ProviderHubHospitalSortColumn.OpportunityGapImpactAscending,
];

const gapImpactSortColumns = new Set<ProviderHubHospitalSortColumn>([
  ...combinedGapImpactSortOptions,
  ...breakdownGapImpactSortOptions,
]);

const ProviderHubHospitalView: FC = () => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const providerHubService = useRef(new ProviderHubService()).current;

  const [sortChangedByDropdown, setSortChangedByDropdown] = useState(false);
  const [showFullHospitalData, setShowFullHospitalData] = useState(false);
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);

  const handleHospitalClick = async (hospital: ProviderHubHospitals) => {
    const details = await providerHubService.getHospitalDetails(
      projectContext?.project?.projectId,
      providerHubContext.filterState.projectRunConfigurationId,
      hospital.ccn
    );
    providerHubContext.navigateToHospitalProfile(details, ProviderProfileReferrerEnum.ProviderHubLandingPage);
  };

  const showPci = providerHubContext.showPciFilters;
  const showMotive = projectContext.project.showMotiveAppropriatenessScore;
  const showCareJourney = projectContext.project.showCareJourneyScores;

  // PCI/Motive/CareJourney sort options only appear when the corresponding data is licensed and enabled.
  const pciSortOptions = new Set<ProviderHubHospitalSortColumn>([
    ProviderHubHospitalSortColumn.FilteredQualityDescending,
    ProviderHubHospitalSortColumn.FilteredQualityAscending,
    ProviderHubHospitalSortColumn.FilteredCostDescending,
    ProviderHubHospitalSortColumn.FilteredCostAscending
  ]);
  const motiveSortOptions = new Set<ProviderHubHospitalSortColumn>([
    ProviderHubHospitalSortColumn.FilteredMotiveAppropriatenessDescending,
    ProviderHubHospitalSortColumn.FilteredMotiveAppropriatenessAscending
  ]);
  const careJourneySortOptions = new Set<ProviderHubHospitalSortColumn>([
    ProviderHubHospitalSortColumn.FilteredCareJourneyCostDescending,
    ProviderHubHospitalSortColumn.FilteredCareJourneyCostAscending,
    ProviderHubHospitalSortColumn.FilteredCareJourneyQualityDescending,
    ProviderHubHospitalSortColumn.FilteredCareJourneyQualityAscending
  ]);

  const isSortOptionVisible = (option: ProviderHubHospitalSortColumn): boolean => {
    if (option === ProviderHubHospitalSortColumn.Unknown) return false;
    if (pciSortOptions.has(option)) return showPci;
    if (motiveSortOptions.has(option)) return showMotive;
    if (careJourneySortOptions.has(option)) return showCareJourney;
    return true;
  };

  // Show the four-column region-status breakdown when the backend reports that this run carries region-status
  // data; otherwise fall back to the single Gap Impact column. These indicators are result-set-level metadata
  // (they cover every page), so paging never changes which columns or Gap Impact sort options are available.
  // The filtered and unfiltered ("Show Full Hospital Data") columns are gated independently.
  const useRegionStatusBreakdown = providerHubContext.providerHubHospitals?.useGapsByRegionStatus ?? false;
  const unfilteredUseRegionStatusBreakdown = providerHubContext.providerHubHospitals?.unfilteredUseGapsByRegionStatus ?? false;

  const gapImpactSortOptions = useMemo<ProviderHubHospitalSortColumn[]>(
    () => useRegionStatusBreakdown ? breakdownGapImpactSortOptions : combinedGapImpactSortOptions,
    [useRegionStatusBreakdown]
  );

  const otherSortOptions = useMemo<ProviderHubHospitalSortColumn[]>(
    () => Object.values(ProviderHubHospitalSortColumn).filter((option) => isSortOptionVisible(option) && !gapImpactSortColumns.has(option)),
    [showPci, showMotive, showCareJourney]
  );

  const sortByOptions = useMemo<ProviderHubHospitalSortColumn[]>(
    () => [...gapImpactSortOptions, ...otherSortOptions],
    [gapImpactSortOptions, otherSortOptions]
  );

  /* oxlint-disable sort-keys */
  const columnConfig: ColumnConfig = {
    nameElement: {
      displayText: "Name",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_hospital-table-name",
      noSort: true
    },
    identifier: {
      displayText: "Identifier",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_hospital-table-identifier",
      noSort: true
    },
    providerSources: {
      displayText: "Provider Sources",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      noSort: true
    },
    unfilteredProviderSources: {
      displayText: "Provider Sources - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      style: { backgroundColor: "#F4F9FF" },
      hidden: !showFullHospitalData
    },
    statuses: {
      displayText: "Statuses",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-status",
      tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a provider",
      noSort: true
    },
    unfilteredStatuses: {
      displayText: "Statuses - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-status",
      tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a provider",
      style: { backgroundColor: "#F4F9FF" },
      hidden: !showFullHospitalData
    },
    gapsImpact: {
      displayText: "Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the facility closes",
      noSort: true,
      hidden: useRegionStatusBreakdown
    },
    unfilteredGapsImpact: {
      displayText: "Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the facility closes",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || unfilteredUseRegionStatusBreakdown
    },
    activeGapImpact: {
      displayText: "Active Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-active-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in active service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredActiveGapImpact: {
      displayText: "Active Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-active-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in active service area counties",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !unfilteredUseRegionStatusBreakdown
    },
    pendingGapImpact: {
      displayText: "Pending Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-pending-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in pending service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredPendingGapImpact: {
      displayText: "Pending Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-pending-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in pending service area counties",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !unfilteredUseRegionStatusBreakdown
    },
    approvedGapImpact: {
      displayText: "Approved Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-approved-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in approved service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredApprovedGapImpact: {
      displayText: "Approved Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-approved-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in approved service area counties",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !unfilteredUseRegionStatusBreakdown
    },
    opportunityGapImpact: {
      displayText: "Opportunity Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-opportunity-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in opportunity-to-expand service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredOpportunityGapImpact: {
      displayText: "Opportunity Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-opportunity-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the hospital closes in opportunity-to-expand service area counties",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !unfilteredUseRegionStatusBreakdown
    },
    servicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-servicing-providers",
      noSort: true
    },
    unfilteredServicingProviders: {
      displayText: "Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-servicing-providers",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData
    },
    servicingSpecialties: {
      displayText: "Servicing Specialty Groups",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-servicing-specialties",
      tooltip: "Indicates the Specialty Groups that the Hospital services",
      noSort: true
    },
    unfilteredServicingSpecialties: {
      displayText: "Servicing Specialty Groups - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-servicing-specialties",
      tooltip: "Indicates the Specialty Groups that the Hospital services",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData
    },
    outOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-oon-providers",
      noSort: true
    },
    unfilteredOutOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-oon-providers",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData
    },
    inNetworkServicingProviders: {
      displayText: "In-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-in-providers",
      noSort: true
    },
    unfilteredInNetworkServicingProviders: {
      displayText: "In-network Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-in-providers",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData
    },
    effectiveness: {
      hidden: !showPci,
      displayText: "Effectiveness (Quality)",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-effectiveness",
      noSort: true
    },
    unfilteredEffectiveness: {
      displayText: "Effectiveness (Quality) - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-effectiveness",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !showPci
    },
    efficiency: {
      hidden: !showPci,
      displayText: "Efficiency (Cost)",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-efficiency",
      noSort: true
    },
    unfilteredEfficiency: {
      displayText: "Efficiency (Cost) - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-efficiency",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !showPci
    },
    motiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" />,
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-motive-appropriateness",
      noSort: true,
      hidden: !showMotive
    },
    unfilteredMotiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" label="Motive Appropriateness - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-motive-appropriateness",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !showMotive
    },
    careJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-carejourney-cost",
      noSort: true,
      hidden: !showCareJourney
    },
    unfilteredCareJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" label="CareJourney Cost - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-carejourney-cost",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !showCareJourney
    },
    careJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-carejourney-quality",
      noSort: true,
      hidden: !showCareJourney
    },
    unfilteredCareJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" label="CareJourney Quality - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_hospital-table-unfiltered-carejourney-quality",
      style: { backgroundColor: "#F4F9FF" },
      noSort: true,
      hidden: !showFullHospitalData || !showCareJourney
    }
  };
  /* oxlint-enable sort-keys */

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => handlePageChange(pageNumber),
    enableBackendPagination: true,
    handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
    initialPage: providerHubContext.pageIndex,
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: providerHubContext.pageSize,
    resetToFirstPage: providerHubContext.resetToFirstPage,
    showPageSizeSelector: true,
    totalRecords: providerHubContext.providerHubHospitals?.totalCount ?? 0
  };

  const handlePageSizeChange = (pageSize: number) => {
    providerHubContext.setPageSize(pageSize);
    providerHubContext.setPageIndex(1);
  };

  const handlePageChange = (newPage: number) => {
    if (providerHubContext.resetToFirstPage) {
      providerHubContext.setResetToFirstPage(false);
    } else {
      providerHubContext.setPageIndex(newPage);
    }
  };

  // Snapshot the search context when results load. The identifier column shows what was actually searched;
  // reading the live filterState here would recompute the table (and re-render it) on every keystroke.
  const [isNpiSearch, setIsNpiSearch] = useState(providerHubContext.filterState.searchByOption === ProviderSearchByEnum.NPI2);
  const [searchInput, setSearchInput] = useState(providerHubContext.filterState.searchByInput);
  useEffect(() => {
    setIsNpiSearch(providerHubContext.filterState.searchByOption === ProviderSearchByEnum.NPI2);
    setSearchInput(providerHubContext.filterState.searchByInput);
  }, [providerHubContext.providerHubHospitals]);

  /* oxlint-disable sort-keys */
  const hospitalTableData = useMemo<ProviderHubHospitalTableData[]>(() => {
    return (providerHubContext.providerHubHospitals?.items ?? []).map((hospital) => ({
      [wafflesKeyColumnName]: hospital.ccn,
      nameElement: (
        <QESButton
          qesType="link"
          qesTestId={`provider-hub-hospital-view__hospital--${hospital.hospitalName}`}
          onClick={() => handleHospitalClick(hospital)}
          className="demibold"
        >
          {hospital.hospitalName ?? "---"}
        </QESButton>
      ),
      name: hospital.hospitalName,
      identifier: isNpiSearch ? `NPI: ${searchInput}` : `CCN: ${hospital.ccn}`,
      providerSources: (
        <>
          {sortProviderNetworkTypes(hospital.providerNetworkTypes ?? []).map((networkType) =>
            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
          )}
        </>
      ),
      unfilteredProviderSources: (
        <>
          {sortProviderNetworkTypes(hospital.unfilteredProviderNetworkTypes ?? []).map((networkType) =>
            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
          )}
        </>
      ),
      statuses: (hospital.providerActionStatuses ?? []).length > 0 ? (
        <>
          {hospital.providerActionStatuses.map((status) =>
            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={status} status={status} />
          )}
        </>
      ) : (
        <>{NO_ACTIONS_APPLIED_LABEL}</>
      ),
      unfilteredStatuses: (hospital.unfilteredProviderActionStatuses ?? []).length > 0 ? (
        <>
          {hospital.unfilteredProviderActionStatuses.map((status) =>
            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={status} status={status} />
          )}
        </>
      ) : (
        <>{NO_ACTIONS_APPLIED_LABEL}</>
      ),
      gapsImpact: formatGapImpactCount(hospital.gapsImpact),
      unfilteredGapsImpact: formatGapImpactCount(hospital.unfilteredGapsImpact),
      activeGapImpact: formatGapImpactCount(hospital.activeGapsImpact),
      unfilteredActiveGapImpact: formatGapImpactCount(hospital.unfilteredActiveGapsImpact),
      pendingGapImpact: formatGapImpactCount(hospital.pendingGapsImpact),
      unfilteredPendingGapImpact: formatGapImpactCount(hospital.unfilteredPendingGapsImpact),
      approvedGapImpact: formatGapImpactCount(hospital.approvedGapsImpact),
      unfilteredApprovedGapImpact: formatGapImpactCount(hospital.unfilteredApprovedGapsImpact),
      opportunityGapImpact: formatGapImpactCount(hospital.opportunityGapsImpact),
      unfilteredOpportunityGapImpact: formatGapImpactCount(hospital.unfilteredOpportunityGapsImpact),
      servicingProviders: hospital.servicingProviders,
      unfilteredServicingProviders: hospital.unfilteredServicingProviders,
      servicingSpecialties: (
        <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={hospital.filteredServicingSpecialties?.map(formatSpecialtyWithCode) ?? []}
          numberOfElementsPerRow={3}
        />
      ),
      unfilteredServicingSpecialties: (
        <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={hospital.unfilteredServicingSpecialties?.map(formatSpecialtyWithCode) ?? []}
          numberOfElementsPerRow={3}
        />
      ),
      outOfNetworkServicingProviders: hospital.outOfNetworkServicingProviders,
      unfilteredOutOfNetworkServicingProviders: hospital.unfilteredOutOfNetworkServicingProviders,
      inNetworkServicingProviders: hospital.inNetworkServicingProviders,
      unfilteredInNetworkServicingProviders: hospital.unfilteredInNetworkServicingProviders,
      effectiveness: hospital.quality ?? "---",
      unfilteredEffectiveness: hospital.unfilteredQuality ?? "---",
      efficiency: hospital.cost ?? "---",
      unfilteredEfficiency: hospital.unfilteredCost ?? "---",
      motiveAppropriateness: hospital.motiveAppropriateness ?? "---",
      unfilteredMotiveAppropriateness: hospital.unfilteredMotiveAppropriateness ?? "---",
      careJourneyCost: hospital.careJourneyCost ?? "---",
      unfilteredCareJourneyCost: hospital.unfilteredCareJourneyCost ?? "---",
      careJourneyQuality: hospital.careJourneyQuality ?? "---",
      unfilteredCareJourneyQuality: hospital.unfilteredCareJourneyQuality ?? "---",
    }));
  }, [providerHubContext.providerHubHospitals, isNpiSearch, searchInput]);
  /* oxlint-enable sort-keys */

  const handleSortChange = (newSortValue: ProviderHubHospitalSortColumn) => {
    providerHubContext.setFilter('sortHospitalColumnAndOrder', newSortValue);
    setSortChangedByDropdown(true);
  };

  useEffect(() => {
    if (sortChangedByDropdown) {
      providerHubContext.handleSearchClick();
    }
    setSortChangedByDropdown(false);
  }, [providerHubContext.filterState.sortHospitalColumnAndOrder, sortChangedByDropdown]);

  // When the display mode flips (combined <-> breakdown) the previously selected Gap Impact sort may
  // no longer appear in the dropdown; reset to the default and re-search so results reflect the reset.
  useEffect(() => {
    const currentSort = providerHubContext.filterState.sortHospitalColumnAndOrder;
    if (!sortByOptions.includes(currentSort)) {
      providerHubContext.setFilter('sortHospitalColumnAndOrder', ProviderHubHospitalSortColumn.NameAscending);
      setSortChangedByDropdown(true);
    }
  }, [sortByOptions]);

  return (
    <PageSection>
      <>
        <ProviderHubBasicFilters />
        <div className="provider-hub-providers-table-info-header">
          <div>
            <h5>Hospitals</h5>
            <p>View and select a hospital listed below</p>
            <QESSelect
              allowEmpty={false}
              className="sort-by-option"
              testId="provider-hub__hospital-sort-by-select"
              label="Sort by"
              onChange={(e) => handleSortChange(e.target.value as ProviderHubHospitalSortColumn)}
              value={providerHubContext.filterState.sortHospitalColumnAndOrder}
            >
              <optgroup label="Gap Impact">
                {gapImpactSortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SortHospitalByOptionToDisplay(option)}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Other">
                {otherSortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SortHospitalByOptionToDisplay(option)}
                  </option>
                ))}
              </optgroup>
            </QESSelect>
          </div>
          {(providerHubContext.providerHubHospitals?.totalCount ?? 0) > 0 && (
            <QESButton
              qesType="primary"
              qesTestId="provider-hub-hospital-view__generate-report"
              onClick={() => setIsGenerateReportModalOpen(true)}
            >
              Generate Report
            </QESButton>
          )}
        </div>
        <div className="provider-hub-hospital-view__record-count">
          {providerHubContext.showFilterToggle && (
            <ShowFullDataToggle
              label={"Show Full Hospital Data"}
              tooltipText={"Hospitals display both the filtered data, based on search criteria, and unfiltered data"}
              active={showFullHospitalData}
              onChange={() => { setShowFullHospitalData(!showFullHospitalData) }}
              testId={"provider-hub-hospital-view_full-hospital-toggle"}
            />
          )}
          <p>Hospitals: {providerHubContext.providerHubHospitals?.totalCount?.toLocaleString() ?? 0}</p>
        </div>
        <Waffles<ProviderHubHospitalTableData>
          columns={columnConfig}
          data={hospitalTableData}
          totalNumberOfRecords={hospitalTableData.length}
          enablePagination={true}
          emptyTableMessage="No matching hospital results found. Please start a new search"
          fullWidth={true}
          loading={providerHubContext.loading}
          paginationProps={paginationProps}
          testId="provider-hub__hospital-table"
          variant="frozen-border"
        />
        <ProviderHubGenerateReportFlyout
          isModalOpen={isGenerateReportModalOpen}
          setIsModalOpen={setIsGenerateReportModalOpen}
          filters={providerHubContext.filterState}
        />
      </>
    </PageSection>
  );
};

export default ProviderHubHospitalView;
